import { DeleteOutlined, EditOutlined, PlusOutlined, ShopOutlined } from '@ant-design/icons';
import {
    Button,
    Col,
    Form,
    Input,
    InputNumber,
    message,
    Modal,
    Row,
    Select,
    Space,
    Tag,
    Typography,
} from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import WebGrid from '../../webgrid/src';
import type { InventoryItem as ApiInventoryItem } from '../contexts/ApiContext';
import { useApi } from '../contexts/ApiContext';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

type InventoryItem = ApiInventoryItem & {
    condition?: string;
    purchase_price?: number;
    estimated_value?: number;
    location?: string;
    notes?: string;
    [key: string]: unknown;
};

const Inventory = () => {
    const [inventory, setInventory] = useState<InventoryItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingItem, setEditingItem] = useState<InventoryItem | null>(null);
    const [form] = Form.useForm();
    const { inventory: inventoryApi } = useApi();
    const { t } = useTranslation();

    const loadInventory = useCallback(
        async (params = {}) => {
            try {
                setLoading(true);
                const result = await inventoryApi.getAll(params);

                if (result.success) {
                    setInventory((result.data?.items ?? []) as InventoryItem[]);
                } else {
                    message.error(result.error ?? 'Failed to load inventory');
                }
            } catch (error) {
                message.error('Network error occurred');
                console.error('Load inventory error:', error);
            } finally {
                setLoading(false);
            }
        },
        [inventoryApi],
    );

    useEffect(() => {
        loadInventory();
    }, [loadInventory]);

    const handleAddItem = () => {
        setEditingItem(null);
        form.resetFields();
        setModalVisible(true);
    };

    const handleEditItem = (item: InventoryItem) => {
        setEditingItem(item);
        form.setFieldsValue({
            name: item.name,
            description: item.description,
            category: item.category,
            condition: item.condition,
            purchase_price: item.purchase_price,
            estimated_value: item.estimated_value,
            status: item.status,
            location: item.location,
            notes: item.notes,
        });
        setModalVisible(true);
    };

    const handleDeleteItem = async (item: InventoryItem) => {
        Modal.confirm({
            title: 'Delete Item',
            content: `Are you sure you want to delete "${item.name}"?`,
            okText: 'Yes',
            okType: 'danger',
            cancelText: 'No',
            onOk: async () => {
                try {
                    const result = await inventoryApi.delete(Number(item.id));
                    if (result.success) {
                        message.success('Item deleted successfully');
                        loadInventory();
                    } else {
                        message.error(result.error ?? 'Failed to delete item');
                    }
                } catch (error) {
                    console.error(error);
                    message.error('Network error occurred');
                }
            },
        });
    };

    const handleModalOk = async () => {
        try {
            const values = await form.validateFields();

            if (editingItem) {
                // Update existing item
                const result = await inventoryApi.update(Number(editingItem.id), values);
                if (result.success) {
                    message.success('Item updated successfully');
                    setModalVisible(false);
                    loadInventory();
                } else {
                    message.error(result.error ?? 'Failed to update item');
                }
            } else {
                // Create new item
                const result = await inventoryApi.create(values);
                if (result.success) {
                    message.success('Item created successfully');
                    setModalVisible(false);
                    loadInventory();
                } else {
                    message.error(result.error ?? 'Failed to create item');
                }
            }
        } catch (error) {
            console.error('Form validation error:', error);
        }
    };

    const handleModalCancel = () => {
        setModalVisible(false);
        form.resetFields();
        setEditingItem(null);
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'available':
                return 'green';
            case 'sold':
                return 'blue';
            case 'reserved':
                return 'orange';
            case 'damaged':
                return 'red';
            default:
                return 'default';
        }
    };

    const getConditionColor = (condition: string) => {
        switch (condition) {
            case 'excellent':
                return 'green';
            case 'good':
                return 'blue';
            case 'fair':
                return 'orange';
            case 'poor':
                return 'red';
            default:
                return 'default';
        }
    };

    const columns = [
        {
            key: 'id',
            title: 'ID',
            dataIndex: 'id',
            sortable: true,
            filterable: false,
        },
        {
            key: 'name',
            title: 'Name',
            dataIndex: 'name',
            sortable: true,
            filterable: true,
        },
        {
            key: 'category',
            title: 'Category',
            dataIndex: 'category',
            sortable: true,
            filterable: true,
        },
        {
            key: 'condition',
            title: 'Condition',
            dataIndex: 'condition',
            sortable: true,
            filterable: true,
            render: (condition: unknown) => (
                <Tag color={getConditionColor(String(condition))}>
                    {String(condition).toUpperCase()}
                </Tag>
            ),
        },
        {
            key: 'purchase_price',
            title: 'Purchase Price',
            dataIndex: 'purchase_price',
            sortable: true,
            filterable: false,
            render: (value: number | string) =>
                value ? `${parseFloat(String(value)).toFixed(2)} LEI` : '-',
        },
        {
            key: 'estimated_value',
            title: 'Estimated Value',
            dataIndex: 'estimated_value',
            sortable: true,
            filterable: false,
            render: (value: number | string) =>
                value ? `${parseFloat(String(value)).toFixed(2)} LEI` : '-',
        },
        {
            key: 'status',
            title: 'Status',
            dataIndex: 'status',
            sortable: true,
            filterable: true,
            render: (status: unknown) => (
                <Tag color={getStatusColor(String(status))}>{String(status)?.toUpperCase()}</Tag>
            ),
        },
        {
            key: 'location',
            title: 'Location',
            dataIndex: 'location',
            sortable: false,
            filterable: true,
        },
        {
            key: 'actions',
            title: 'Actions',
            dataIndex: 'actions',
            sortable: false,
            filterable: false,
            render: (value: unknown, row: InventoryItem) => (
                <Space size="small">
                    <Button
                        type="primary"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleEditItem(row)}
                    >
                        Edit
                    </Button>
                    <Button
                        type="primary"
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => handleDeleteItem(row)}
                    >
                        Delete
                    </Button>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <div
                className="page-header"
                style={{ ['--page-header-accent' as string]: '#16a34a' } as React.CSSProperties}
            >
                <div className="page-header__lead">
                    <span className="page-header__icon">
                        <ShopOutlined aria-hidden />
                    </span>
                    <div>
                        <Title level={2} style={{ margin: 0 }}>
                            {t('inventory.title')}
                        </Title>
                        <Text type="secondary">{t('inventory.subtitle')}</Text>
                    </div>
                </div>
                <div className="page-header__actions">
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAddItem}>
                        {t('inventory.add')}
                    </Button>
                </div>
            </div>

            <WebGrid<InventoryItem>
                data={inventory}
                columns={columns}
                loading={loading}
                pagination={true}
                pageSize={20}
                filterBar={true}
                exportable={true}
                selectable={false}
                onRowClick={() => {}}
                onRowDoubleClick={handleEditItem}
                onExport={() => {}}
                height="600px"
                emptyMessage={t('inventory.noData')}
            />

            <Modal
                title={editingItem ? t('inventory.edit') : t('inventory.add')}
                open={modalVisible}
                onOk={handleModalOk}
                onCancel={handleModalCancel}
                width={800}
                okText={editingItem ? t('inventory.edit') : t('inventory.add')}
            >
                <Form form={form} layout="vertical" name="inventoryForm">
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="name"
                                label="Item Name"
                                rules={[{ required: true, message: 'Please input the item name!' }]}
                            >
                                <Input placeholder="Enter item name" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="category"
                                label="Category"
                                rules={[{ required: true, message: 'Please select a category!' }]}
                            >
                                <Select placeholder="Select category">
                                    <Option value="jewelry">Jewelry</Option>
                                    <Option value="electronics">Electronics</Option>
                                    <Option value="tools">Tools</Option>
                                    <Option value="watches">Watches</Option>
                                    <Option value="musical_instruments">Musical Instruments</Option>
                                    <Option value="collectibles">Collectibles</Option>
                                    <Option value="other">Other</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="description" label="Description">
                        <TextArea rows={3} placeholder="Detailed description of the item" />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item
                                name="condition"
                                label="Condition"
                                rules={[{ required: true, message: 'Please select condition!' }]}
                            >
                                <Select placeholder="Select condition">
                                    <Option value="excellent">Excellent</Option>
                                    <Option value="good">Good</Option>
                                    <Option value="fair">Fair</Option>
                                    <Option value="poor">Poor</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="status"
                                label="Status"
                                rules={[{ required: true, message: 'Please select status!' }]}
                            >
                                <Select placeholder="Select status">
                                    <Option value="available">Available</Option>
                                    <Option value="sold">Sold</Option>
                                    <Option value="reserved">Reserved</Option>
                                    <Option value="damaged">Damaged</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="location" label="Location">
                                <Input placeholder="Storage location" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="purchase_price" label="Purchase Price (LEI)">
                                <InputNumber
                                    style={{ width: '100%' }}
                                    placeholder="0.00"
                                    min={0}
                                    precision={2}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="estimated_value" label="Estimated Value (LEI)">
                                <InputNumber
                                    style={{ width: '100%' }}
                                    placeholder="0.00"
                                    min={0}
                                    precision={2}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="notes" label="Notes">
                        <TextArea rows={2} placeholder="Additional notes" />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default Inventory;
